import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ImageIcon, Link as LinkIcon, Send, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ManualPostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "manual_telegram" or "manual_meta" — sent as edge function `type` */
  type: "manual_telegram" | "manual_meta";
  title: string;
  /** Description shown above the form */
  description?: string;
  onPosted?: () => void;
}

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const ManualPostDialog = ({ open, onOpenChange, type, title, description, onPosted }: ManualPostDialogProps) => {
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const reset = () => {
    setText(""); setLink(""); setImageFile(null); setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (file: File) => {
    if (!/^image\//.test(file.type)) {
      toast({ title: "Invalid file", description: "Please choose an image.", variant: "destructive" });
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast({ title: "Too large", description: "Max 8MB for inline upload.", variant: "destructive" });
      return;
    }
    setImageFile(file);
    setImagePreview(await fileToDataUrl(file));
  };

  const send = async () => {
    if (!text.trim() && !imageFile) {
      toast({ title: "Nothing to post", description: "Add a caption or image.", variant: "destructive" });
      return;
    }
    if (type === "manual_meta" && !imageFile && link) {
      toast({
        title: "Heads up",
        description: "Instagram needs an image. Without one, only Facebook will be posted.",
      });
    }
    setSending(true);
    try {
      const imageDataUrl = imageFile ? await fileToDataUrl(imageFile) : null;
      const { data, error } = await supabase.functions.invoke("daily-channel-post", {
        body: { type, force: true, text: text.trim(), imageDataUrl, link: link.trim() || null },
      });
      if (error) throw error;
      const r = (data as any)?.results?.[type];
      if ((data as any)?.ok === false) {
        toast({ title: "Failed", description: (data as any).reason || "Could not post.", variant: "destructive" });
      } else {
        toast({ title: "📨 Posted", description: r || "Sent." });
        reset();
        onOpenChange(false);
        onPosted?.();
      }
    } catch (e: any) {
      toast({ title: "Failed", description: e.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-4 h-4" /> {title}
          </DialogTitle>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="manual-text" className="text-xs">Caption</Label>
            <Textarea
              id="manual-text"
              placeholder="Write your message…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={6}
              className="resize-none"
            />
            <p className="text-[10px] text-muted-foreground text-right">{text.length} chars</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> Image {type === "manual_meta" && <span className="text-muted-foreground">(required for Instagram)</span>}
            </Label>
            {imagePreview ? (
              <div className="relative inline-block">
                <img src={imagePreview} alt="preview" className="rounded-md max-h-40 border" />
                <button
                  type="button"
                  onClick={() => { setImageFile(null); setImagePreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1"
                  aria-label="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <Input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                className="text-xs"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="manual-link" className="text-xs flex items-center gap-1.5">
              <LinkIcon className="w-3 h-3" /> Optional link
            </Label>
            <Input
              id="manual-link"
              type="url"
              placeholder="https://…"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="text-xs"
            />
            {type === "manual_meta" && (
              <p className="text-[10px] text-muted-foreground">
                Instagram captions can't be clickable — link will appear as plain text.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending}>
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</> : <><Send className="w-4 h-4 mr-2" /> Post now</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualPostDialog;
